// frontend/src/components/capacitor/CapacitorShell.tsx
import { useState } from 'react'
import { WhatsAppMessage, PreviewOptions, ChatStats, ParseQuality } from '../../types/preview'
import { BillingProduct } from '../../hooks/useBilling'
import { CapacitorHome } from './CapacitorHome'
import { CapacitorProcessing } from './CapacitorProcessing'
import { CapacitorPreview } from './CapacitorPreview'
import { CapacitorHandoff } from './CapacitorHandoff'
import { CapacitorSuccess } from './CapacitorSuccess'
import './Capacitor.css'

type CapacitorScreen = 'home' | 'preview' | 'handoff' | 'success'

interface UploadStatus {
  jobId: string | null
  status: 'idle' | 'uploading' | 'processing' | 'completed' | 'error'
  progress: number
  message: string
  result?: any
}

interface Props {
  uploadStatus: UploadStatus
  previewMessages: WhatsAppMessage[]
  previewStats: ChatStats
  previewQuality?: ParseQuality | null
  previewOptions: PreviewOptions
  setPreviewOptions: (opts: PreviewOptions) => void
  onUpload: (file: File) => void
  onReset: () => void
  onPurchase: (productId: string) => void
  billingProducts: BillingProduct[]
  isRendering: boolean
  paymentSuccessJobId: string | null
}

export function CapacitorShell({
  uploadStatus,
  previewMessages,
  previewStats,
  previewQuality,
  previewOptions,
  setPreviewOptions,
  onUpload,
  onReset,
  onPurchase,
  billingProducts,
  isRendering,
  paymentSuccessJobId,
}: Props) {
  const [screen, setScreen] = useState<CapacitorScreen>('home')

  const isLoading =
    uploadStatus.status === 'uploading' || uploadStatus.status === 'processing'

  const activeScreen: CapacitorScreen = (() => {
    if (paymentSuccessJobId) return 'success'
    if (uploadStatus.status === 'completed' && screen !== 'handoff') return 'preview'
    if (screen === 'handoff') return 'handoff'
    return 'home'
  })()

  return (
    <div className="cap-shell">
      {activeScreen === 'home' && (
        <>
          {isLoading ? (
            <CapacitorProcessing
              progress={uploadStatus.progress}
              message={uploadStatus.message}
            />
          ) : (
            <CapacitorHome onFile={onUpload} />
          )}
        </>
      )}

      {activeScreen === 'preview' && (
        <CapacitorPreview
          messages={previewMessages}
          stats={previewStats}
          quality={previewQuality}
          options={previewOptions}
          setOptions={setPreviewOptions}
          messageCount={uploadStatus.result?.message_count ?? previewMessages.length}
          onPurchase={onPurchase}
          billingProducts={billingProducts}
          isRendering={isRendering}
          onDesktopHandoff={() => setScreen('handoff')}
        />
      )}

      {activeScreen === 'handoff' && uploadStatus.jobId && (
        <CapacitorHandoff
          jobId={uploadStatus.jobId}
          onBack={() => setScreen('preview')}
        />
      )}

      {activeScreen === 'success' && paymentSuccessJobId && (
        <CapacitorSuccess
          renderJobId={paymentSuccessJobId}
          onNewExport={onReset}
        />
      )}
    </div>
  )
}
